/**
 * ===============[ TABLE OF CONTENTS ]===============
 * 0. Helpers 
 *   0.1 Filter Object 
 * 
 * 1. Functions
 *   1.2 generateRandomNumber()
 *   1.3 shuffle_array()
 *   1.5 Array2Table
 *   1.5 humanize 
 *   1.6 dehumanize
 *   1.8 formatDate
 *   1.9 formatDuration
 *   1.9 sortAnObject
 *   1.10 sortArrayOfObjects
 *   1.11 isObject
 *   1.12 deepEqual
 *   1.13 diffObjects
 *   1.14 sleep 
 *   1.15 getParams 
 *   1.16 renameKeys
 *   1.17 massageData 
 *   1.18 splitArrayChunks
 *   1.19 getUrlPath 
 *   1.20 validParams 
 *   1.21 generateRandomPassword 
 *   1.22 handleTableRowClick
 *   1.23 humanFileSize
 *   1.24 formatPhoneNumber
 *   1.25 removeHttp
 *   1.26 removeEmptyFromObj 
 * 
 ******************************************************/
import moment from "moment-timezone";

/* ===============[ 0. Helpers ]======================*/
/**
 * 0.1 Filter Object 
 */
Object.filter = (obj, predicate) => Object.fromEntries(Object.entries(obj).filter(predicate));
const filterObject = (obj, predicate) => {
  return Object.filter(obj, predicate);
};

/* ===============[ 1. Functions ]====================*/
/**
* 1.2 generateRandomNumber
* @param {int} min
* @param {max} max
*/
const generateRandomNumber = (min = 0, max = 10) => {
  return Math.floor(Math.random() * Math.floor(+max - +min)) + +min;
};

/**
* 1.3 shuffle_array
* @param {array} some_array
* @return {array} some_array - returns the shuffled version.
*/
const shuffle_array = (some_array) => {
  return some_array.sort(() => Math.random() - 0.5);
};

/**
 * 1.4 Array2Table
 * @param {array} arr - array of objects
 */
const Array2Table = (arr) => {
  let Table = [];
  let top_row = [];
  let rows = [];

  for (let i = 0; i < arr.length; i++) {
    let cells = [];

    for (let property in arr[i]) {
      if (top_row.length < Object.keys(arr[i]).length) {
        top_row.push(`<th scope="col">${property}</th>`);
      }
      if (arr[i][property] === null) {
        cells.push(`<td>${null}</td>`);
      } else {
        cells.push(`<td>${arr[i][property]}</td>`);
      }
    }

    rows.push(`<tr>${cells.join("")}</tr>`);
  }

  Table.push(`<table>`);
  Table.push(`<thead>${top_row.join("")}</thead>`);
  Table.push(`<tbody>${rows.join("")}<tbody>`);
  Table.push("</table>");
  return Table.join("");
}

/**
 * 1.5 humanize 
 * ex: request_id = Request Id
 * 
 * @param {string} str 
 * @return Humanized String
 */
const humanize = (str, allCaps = false, trailingSpaces = false) => {
  if (typeof str === 'boolean') {
    return str ? "true" : "false";
  }
  if (str === null) {
    return "";
  }
  if (str === undefined || typeof str === 'undefined') {
    return "undefined";
  }
  if (Array.isArray(str)) {
    return str.length === 0 ? "[]" : str.join(", ");
  }
  if (typeof str === 'object') {
    return Object.entries(str).map(([key, value]) => `${key} = ${value}`).join(", ");
  }
  if (typeof str === 'number') {
    return str.toString();
  }

  if (trailingSpaces) {
    str = `${str}`.split(" ").map((word) => (allCaps ? word.toLocaleUpperCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())).join(" ");
    return str;
  }

  const dateFields = ['download_expires_at', 'last_downloaded_timestamp', 'createdAt', 'updatedAt'];
  let i, frags = /[_]/g.test(str.toString().trim()) ? str.toString().trim().split('_') :
    (/[ ]/g.test(str.toString().trim()) ? str.toString().trim().split(' ') :
      [str.toString().trim()]);

  for (i = 0; i < frags.length; i++) {
    if (frags[i].indexOf("/") > -1) continue; // Ignore any word with forward slash

    // Check for words 'id', 'bso', or 'ocr' and make sure it's capitalized. 
    if (['id', 'bso', 'ocr', 'sftp', 's3', 'ere', 'tfa', 'ssn', 'rqid'].includes(frags[i].toLowerCase()) || allCaps) {
      frags[i] = frags[i].toUpperCase();
    } else {
      frags[i] = frags[i].charAt(0).toUpperCase() + (dateFields.includes(str) ? frags[i].slice(1) : frags[i].slice(1).toLowerCase());
    }
  }

  return frags.join(' ');
}

/**
 * 1.6 dehumanize
 * Does the opposite of humanize
 * @param {string} str 
 * @return Dehumanized String
 */
const dehumanize = (str) => {
  if (typeof str !== "string") return str;
  let implodedStr = str.split(' ').join('_');
  return implodedStr.toLowerCase();
}

/**
 * 1.7 formatDate
 * - Detect client timezone and convert date accordingly 
 * 
 * @param {*} timestamp 
 * @param {*} formatString 
 */
const formatDate = (timestamp = Date.now(), formatString = "MMMM D, YYYY hh:mm:ss A", tZone = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
  if (!timestamp) return timestamp; // Invalid timestamp
  if (tZone === null) {
    return moment(timestamp).format(formatString);
  }
  return moment(timestamp).tz(tZone).format(formatString);
}

/**
 * 1.8 formatDuration
 * Can accept milliseconds but if the value is less than 1 second you'll get 00:00:00 
 * 
 * @param {integer} value
 * @param {string} units - must be milliseconds, seconds, minutes, or hours 
 * @returns Returns duration in the format HOURS:MINUTES:SECONDS 
 */
const formatDuration = (value, units = 'seconds') => {
  let allowed_units = ['milliseconds', 'seconds', 'minutes', 'hours'];
  if (allowed_units.includes(units) === false) {
    console.log(`Unit ${units} is not allowed. The allowed units are ${allowed_units.join(" ")}`);
    return value;
  }

  return moment.utc(moment.duration(value, units).asMilliseconds()).format("HH:mm:ss");
}

/**
 * 1.9 sortAnObject
 * @param {*} unsortedObject 
 * @param {*} sortOrder 
 */
const sortAnObject = (unsortedObject, sortOrder) => {
  if (sortOrder === undefined) return unsortedObject;
  let orderedResults = {};

  sortOrder.forEach((key) => {
    if (key in unsortedObject) {
      orderedResults[key] = unsortedObject[key];
    }
  });

  return {
    ...orderedResults,
    ...unsortedObject
  };
};

/**
 * 1.10 sortArrayOfObjects
 * 
 * @param {array} unsortedArrayOfObjects 
 * @param {array|string} sortOrder - if array is passed then sort by this array order. 
 * If string is passed then sort by this property ASC or DESC depending on the order.
 * @param {string} order - ASC or DESC
 * @return {array} sortedArrayOfObjects
 */
const sortArrayOfObjects = (unsortedArrayOfObjects, sortOrder, order = "ASC") => {
  if (!unsortedArrayOfObjects || !sortOrder) return unsortedArrayOfObjects;

  if (Array.isArray(sortOrder)) {
    return unsortedArrayOfObjects.map((item) => sortAnObject(item, sortOrder));
  }

  if (typeof sortOrder !== "string" || typeof order !== "string" || !unsortedArrayOfObjects[0][sortOrder]) return unsortedArrayOfObjects;

  if (order.toUpperCase() === "ASC") {
    return unsortedArrayOfObjects.sort((a, b) => (a[sortOrder] > b[sortOrder]) ? 1 : -1);
  } else {
    return unsortedArrayOfObjects.sort((a, b) => (a[sortOrder] > b[sortOrder]) ? -1 : 1);
  }
};

// 1.11 isObject
const isObject = (object) => {
  return object != null && typeof object === 'object';
}

// 1.12 deepEqual
const deepEqual = (object1, object2) => {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const val1 = object1[key];
    const val2 = object2[key];
    const areObjects = isObject(val1) && isObject(val2);
    if (
      (areObjects && !deepEqual(val1, val2)) || (!areObjects && val1 !== val2)
    ) {
      return false;
    }
  }

  return true;
}

/**
 * 1.13 diffObjects
 * 
 * Find the differences between two objects and push to a new object
 * (c) 2019 Chris Ferdinandi & Jascha Brinkmann, MIT License, https://gomakethings.com & https://twitter.com/jaschaio
 * @param  {Object} obj1 The original object
 * @param  {Object} obj2 The object to compare against it
 * @return {Object}      An object of differences between the two
 */
const diffObjects = (obj1, obj2) => {
  // Make sure an object to compare is provided
  if (!obj2 || Object.prototype.toString.call(obj2) !== '[object Object]') {
    return obj1;
  }

  let diffs = {};
  let key;

  /**
   * Check if two arrays are equal
   * @param  {Array}   arr1 The first array
   * @param  {Array}   arr2 The second array
   * @return {Boolean}      If true, both arrays are equal
   */
  const arraysMatch = (arr1, arr2) => {
    // Check if the arrays are the same length
    if (arr1.length !== arr2.length) return false;

    // Check if all items exist and are in the same order
    for (var i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) return false;
    }

    // Otherwise, return true
    return true;
  };

  /**
   * Compare two items and push non-matches to object
   * @param  {*}      item1 The first item
   * @param  {*}      item2 The second item
   * @param  {String} key   The key in our object
   */
  const compare = (item1, item2, key) => {
    // Get the object type
    let type1 = Object.prototype.toString.call(item1);
    let type2 = Object.prototype.toString.call(item2);

    // If type2 is undefined it has been removed
    if (type2 === '[object Undefined]') {
      diffs[key] = null;
      return;
    }

    // If items are different types
    if (type1 !== type2) {
      diffs[key] = item2;
      return;
    }

    // If an object, compare recursively
    if (type1 === '[object Object]') {
      let objDiff = diffObjects(item1, item2);
      if (Object.keys(objDiff).length > 0) {
        diffs[key] = objDiff;
      }
      return;
    }

    // If an array, compare
    if (type1 === '[object Array]') {
      if (!arraysMatch(item1, item2)) {
        diffs[key] = item2;
      }
      return;
    }

    // Else if it's a function, convert to a string and compare
    // Otherwise, just compare
    if (type1 === '[object Function]') {
      if (item1.toString() !== item2.toString()) {
        diffs[key] = item2;
      }
    } else {
      if (item1 !== item2) {
        diffs[key] = item2;
      }
    }
  };

  // Compare our objects
  // Loop through the first object
  for (key in obj1) {
    if (obj1.hasOwnProperty(key)) {
      compare(obj1[key], obj2[key], key);
    }
  }

  // Loop through the second object and find missing items
  for (key in obj2) {
    if (obj2.hasOwnProperty(key)) {
      if (!obj1[key] && obj1[key] !== obj2[key]) {
        diffs[key] = obj2[key];
      }
    }
  }

  // Return the object of differences
  return diffs;
};

/**
 * 1.14 sleep
 * @param {integer} seconds 
 */
const sleep = (seconds = 1) => new Promise(resolve => setTimeout(resolve, seconds * 1000));

/**
 * 1.15 getParams
 * @param {object} params 
 */
const getParams = (params) => isObject(params) ? "?" + Object.entries(params).map(([key, value]) => `${key}=${value.toString().trim().replace(/ /g, '%20')}`).join("&") : "";

/**
 * 1.16 renameKeys
 * Renames object keys to new object based on the keysMap 
 * 
 * @param {object} keysMap - object in the format of { oldkey: newkey }
 * @param {object} obj - object with keys that need to be renamed
 * @return {object} new object based on the keysMap 
 */
const renameKeys = (keysMap, obj) => {
  return Object.keys(obj).reduce((acc, key) => ({
    ...acc,
    ...{ [keysMap[key] || key]: obj[key] }
  }), {});
}

/**
 * 1.17 massageData
 * @param {Array} rawData - array of objects
 * @param {Array} keepFields - array of fields to keep
 * @param {Object} renamedFields - object of renamed fields
 * @param {Array} specialFields - array of fields to massage
 * @param {Array} specialFieldColumns - array of columns to massage
 * @returns 
 */
const massageData = (rawData = [], keepFields = [], renamedFields = {}, dateFields = [], specialFields = [], specialFieldColumns = [], formatStr = "MMMM D, YYYY hh:mm:ss A", timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
  const formatSpecialField = (field, obj, dFieldArray = dateFields, sFieldArray = specialFields, sFieldColumns = specialFieldColumns, formatString = formatStr, tZone = timeZone) => {
    if (!isObject(obj) || Object.keys(obj).length === 0) {
      return field;
    }
    if (!Array.isArray(dFieldArray)) {
      dFieldArray = [];
    }
    if (!sFieldArray.includes(field)) {
      return (dFieldArray.length > 0 && dFieldArray.includes(field)) ? formatDate(obj[field], formatString, tZone) : obj[field];
    }

    return sFieldColumns[field].reduce((acc, sField) => {
      let value = '';
      if (isObject(obj[field]) && sField in obj[field]) {
        value = obj[field][sField] !== null ? obj[field][sField] : '';
      } else if (isObject(obj) && sField in obj) {
        value = obj[sField] !== null ? obj[sField] : '';
        delete obj[sField];
      } else {
        value = sField;
      }
      acc.push(value);
      return acc;
    }, []).join(" ");
  };

  if (!Array.isArray(rawData)) {
    rawData = [rawData];
  }

  if ((!keepFields || Array.isArray(keepFields)) && (keepFields.length === 0 || !rawData || Array.isArray(rawData)) && rawData.length === 0) {
    return rawData;
  }

  return rawData.reduce((acc, curr) => {
    let newRow = keepFields.reduce((a, f) => {
      if (f in curr) {
        a[f] = formatSpecialField(f, curr);
      } else if (Object.values(renamedFields).includes(f)) {
        let renamedIndex = Object.values(renamedFields).indexOf(f);
        let field = Object.keys(renamedFields)[renamedIndex];
        if (field in curr) {
          curr[f] = curr[field]; // rename the field
          delete curr[field]; // delete the old field
          a[f] = formatSpecialField(f, curr);
        }
      }
      return a;
    }, {});

    if (Object.keys(newRow).length) {
      acc.push(newRow);
    }
    return acc;
  }, []);
};

/**
 * 1.18 splitArrayChunks
 * Splits the array into chunks of chunkSize specified
 * 
 * @param {*} array 
 * @param {*} chunkSize 
 */
const splitArrayChunks = (array, chunkSize) => {
  let chunks = [], i = 0, n = array.length;
  while (i < n) {
    chunks.push(array.slice(i, i += chunkSize));
  }
  return chunks;
};

/**
 * 1.19 getUrlPath
 * http://localhost:3000/eredocs/some/path/ 
 * will return "some/path"
 * @param {*} urlStr 
 * @returns 
 */
const getUrlPath = (urlStr) => {
  const regex = /(http[s]?:\/\/)?([^/\s]+\/)(.*)/;
  let m;
  let urlPath = '';
  if ((m = regex.exec(urlStr)) !== null) {
    // if urlStr does not contain http then index 1 will be undefined and we just want to keep the original urlStr at index 0
    let desiredIndex = m[1] ? 3 : 0;
    m.forEach((match, groupIndex) => {
      if (groupIndex === desiredIndex) {
        urlPath = match;
      }
    });
  }
  return urlPath;
};

/**
 * 1.20 validParams
 * @param {object} params 
 * @param {array} requiredFields - fields to check for in params 
 * @param {string} rule - any: TRUE Some of the requiredFields exist in params, !any: TRUE when all of the requiredFields exist in params 
 *   
 * @returns 
 */
const validParams = (params, requiredFields = [], rule = 'any') => {
  if (rule === 'any') { // TRUE Some of the requiredFields exist in params 
    return requiredFields.some((field) => (Object.keys(params).includes(field)));
  } else {  // TRUE when all of the requiredFields exist in params 
    return requiredFields.every((field) => (params[field] !== undefined && Object.keys(params).includes(field)));
  }
}

/**
 * 1.21 generateRandomPassword
 * @param {object} params 
 *   hasLength: {number} - length of the password 
 *   countCapitals: {number} - number of capitol letters to include in the password
 *   countSpecials: {number} - number of special characters to include in the password
 *   hasUpper: {boolean} - include uppercase letters in the password
 *   hasSpecial: {boolean} - include special letters in the password
 *   possibleForwardSlash: {boolean} - include forward slash in the password
 *   possibleBackSlash: {boolean} - include back slash in the password
 *   method: {string} - 'crypto' will use the crypto method, anything else will use the Math random method
 *   showDebug: {boolean} - show debug messages
 * @returns randomPassword string 
 */
const generateRandomPassword = ({ hasLength = 10, countCapitals = 1, countSpecials = 1, hasUpper = true, hasSpecial = true, possibleForwardSlash = false, possibleBackSlash = false, method = 'crypto', showDebug = false } = {}) => {
  // Validate Parameters
  typeof hasLength !== 'number' && (hasLength = Number(hasLength));
  isNaN(hasLength) && (hasLength = 10);
  hasLength > 128 && (hasLength = 128);
  typeof countCapitals !== 'number' && (countCapitals = Number(countCapitals));
  isNaN(countCapitals) && (countCapitals = 1);
  countCapitals >= hasLength && (countCapitals = Math.round(hasLength / 3));
  typeof countSpecials !== 'number' && (countSpecials = Number(countSpecials));
  isNaN(countSpecials) && (countSpecials = 1);
  countSpecials >= hasLength && (countSpecials = Math.round(hasLength / 3));

  // Initialize Random Password
  let randomPassword = (method === 'crypto') ? crypto.randomBytes(64).toString('hex').slice(-hasLength) : Math.random().toString(36).slice(-hasLength);
  if (hasUpper || hasSpecial) {
    // Set Capital letter at random index
    let randomIndexOne, ri; // Defined early to avoid scope issues later 
    if (hasUpper) {
      let possibleChar = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
      for (let i = 0; i < countCapitals; i++) {
        let randomUpper = possibleChar.charAt(Math.floor(Math.random() * possibleChar.length));
        randomIndexOne = Math.floor(Math.random() * randomPassword.length);
        if (randomIndexOne === ri) { // Make sure randomIndexOne is not the same as ri
          randomIndexOne === randomPassword.length - 1 ? randomIndexOne-- : randomIndexOne++;
        }
        ri = randomIndexOne; // used for next iteration test 
        showDebug && console.log(`${randomPassword} is the password! Setting upper character ${randomUpper} at index ${randomIndexOne}`);
        randomPassword = randomPassword.substring(0, randomIndexOne) + randomUpper + randomPassword.substring(randomIndexOne + 1);
      }
    } // End of hasUpper

    // Set Special letter at random index
    if (hasSpecial) {
      let specialCharacters = `!@#$%^&*()_+-=[]{};'":|,.<>?`; // NOTE: Not including forward slash or backslash 
      if (possibleForwardSlash) {
        let randomIndex = Math.floor(Math.random() * specialCharacters.length);
        specialCharacters = specialCharacters.substring(0, randomIndex) + `/` + specialCharacters.substring(randomIndex + 1);
      }
      if (possibleBackSlash) {
        let randomIndex = Math.floor(Math.random() * specialCharacters.length);
        specialCharacters = specialCharacters.substring(0, randomIndex) + `\\` + specialCharacters.substring(randomIndex + 1);
      }
      showDebug && console.log(`specialCharacters: ${specialCharacters}`);
      for (let i = 0; i < countSpecials; i++) {
        let randomSpecial = specialCharacters.charAt(Math.floor(Math.random() * specialCharacters.length));
        let randomIndexTwo = Math.floor(Math.random() * randomPassword.length);
        if (randomIndexTwo === randomIndexOne) { // Make sure randomIndexTwo is not the same as randomIndexOne
          randomIndexTwo === randomPassword.length - 1 ? randomIndexTwo-- : randomIndexTwo++;
        }
        randomIndexOne = randomIndexTwo; // used for next iteration test 
        showDebug && console.log(`${randomPassword} is the password! Setting special character ${randomSpecial} at index ${randomIndexTwo}`);
        randomPassword = randomPassword.substring(0, randomIndexTwo) + randomSpecial + randomPassword.substring(randomIndexTwo + 1);
      }
    } // End of hasSpecial
  } // End of if (hasUpper || hasSpecial)

  showDebug && console.log(`${randomPassword} is the password!`);
  return randomPassword;
}

/**
 * 1.22 handleTableRowClick
 * Example Usage, 
 * onClick={(e) => (handleTableRowClick.call(null, e, rowClick))}
 * @param {*} event 
 * @returns 
 */
const handleTableRowClick = (event, cb = () => { }) => {
  if (!event) return;
  event.preventDefault();
  event.stopPropagation(); // Prevent other events from firing on click
  let clickedElement = event?.target || {};
  let { parentElement, dataset, tagName, type, innerText, href, id } = clickedElement;

  // Go up one level in the DOM but preserve innerText
  if (tagName !== "TD" && parentElement?.tagName === "TD") {
    clickedElement = parentElement;
    parentElement = clickedElement.parentElement;
    dataset = { ...dataset, ...clickedElement?.dataset };
    tagName = clickedElement?.tagName;
    if (!innerText && clickedElement?.innerText) {
      innerText = clickedElement.innerText;
    }
  }

  let rowObject = {
    innerText,
    tagName,
    ...(type && { type }),
    ...(href && { href }),
    ...(id && { id }),
    ...dataset,
    ...parentElement?.dataset,
  };

  // Extract row items and save to rowObject and selectedItem
  let selectedItem = {};
  if (tagName === "TD") {
    let tdArray = Array.from(parentElement.querySelectorAll("td")).map(({ innerText }) => innerText).filter(Boolean) || [];
    let thArray = Array.from(parentElement.closest("table").querySelectorAll("thead tr th")).map(({ innerText }) => dehumanize(innerText)).filter(Boolean) || [];

    if ('col' in rowObject) { // Extract selectedItem
      let col = Number(rowObject.col);
      if (col && innerText === tdArray[col]) {
        selectedItem[thArray[col]] = tdArray[col];
      }
    }

    // Match thArray length with tdArray length
    if (tdArray.length > thArray.length) {
      // slice extra elements to match thArray length
      tdArray = tdArray.slice(0, thArray.length);
    } else if (tdArray.length < thArray.length) {
      // Fill in the blanks with null values to match thArray length
      let startIndex = tdArray.length;
      tdArray.length = thArray.length;
      tdArray.fill(null, startIndex);
    }

    if (tdArray.length === thArray.length) {
      for (let i = 0; i < thArray.length; i++) {
        rowObject[thArray[i]] = tdArray[i];
      }
    }
  }

  // Extract selectedItem if failed to do so above
  if ('field' in rowObject) {
    let field = dehumanize(rowObject.field); // We must dehumanize this field since that's what happened to all of values in thArray (see above)
    if (!selectedItem[field] && rowObject[field] === innerText) {
      selectedItem[field] = rowObject[field];
    }
  }

  // Object.keys(rowObject).length > 0 && console.log("handleTableRowClick", rowObject);
  // Object.keys(selectedItem).length > 0 && console.log("selectedItem", selectedItem);
  return cb(rowObject, selectedItem);
};

/**
 * 1.23 humanFileSize
 * Format bytes as human-readable text.
 * 
 * @param {integer} bytes Number of bytes.
 * @param {boolean} si True to use metric (SI) units, aka powers of 1000. False to use binary (IEC), aka powers of 1024.
 * @param {integer} dp Number of decimal places to display.
 * @returns Formatted string.
 */
const humanFileSize = (bytes, si = false, dp = 1) => {
  (typeof bytes !== 'number') && (bytes = Number(bytes));
  const thresh = si ? 1000 : 1024;
  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

  return bytes.toFixed(dp) + ' ' + units[u];
}

/**
 * 1.24 formatPhoneNumber
 * @param {*} phoneNumberString 
 * @returns {string} (111) 111-1111
 */
const formatPhoneNumber = (phoneNumberString = '') => {
  if (!phoneNumberString) {
    return phoneNumberString;
  }

  let cleaned = `${phoneNumberString}`.replace(/\D/g, '');
  if (cleaned.length < 10) {
    return phoneNumberString;
  }

  let match = cleaned.match(/^(\d|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    let intlCode = (match[1] ? `+${match[1]} ` : '');
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
  }
  return phoneNumberString;
}

/**
 * 1.25 removeHttp
 * @param {*} url 
 * @returns 
 */
const removeHttp = (url) => {
  return url.replace(/^https?:\/\//, '');
}

/**
 * 1.26 removeEmptyFromObj
 * Remove empty values from object
 * @param {object} obj 
 * @returns 
 */
const removeEmptyFromObj = (obj) => {
  for (let propName in obj) {
    if (obj[propName] === null || obj[propName] === undefined || obj[propName] === '') {
      delete obj[propName];
    }
  }
  return obj
};

const Utils = {
  filterObject,
  generateRandomNumber,
  shuffle_array,
  Array2Table,
  humanize,
  dehumanize,
  formatDate,
  formatDuration,
  sortAnObject,
  sortArrayOfObjects,
  isObject,
  deepEqual,
  diffObjects,
  sleep,
  getParams,
  renameKeys,
  massageData,
  splitArrayChunks,
  getUrlPath,
  validParams,
  generateRandomPassword,
  handleTableRowClick,
  humanFileSize,
  formatPhoneNumber,
  removeHttp,
  removeEmptyFromObj,
};

export {
  filterObject,
  generateRandomNumber,
  shuffle_array,
  Array2Table,
  humanize,
  dehumanize,
  formatDate,
  formatDuration,
  sortAnObject,
  sortArrayOfObjects,
  isObject,
  deepEqual,
  diffObjects,
  sleep,
  getParams,
  renameKeys,
  massageData,
  splitArrayChunks,
  getUrlPath,
  validParams,
  generateRandomPassword,
  handleTableRowClick,
  humanFileSize,
  formatPhoneNumber,
  removeHttp,
  removeEmptyFromObj,
};
export default Utils;

/* NOTE: getParams can be accessed by any of the following methods
import { getParams } from "../utils/"; // Because OF the 'export' above

import Utils from "../utils"; // Because of the 'default export'
const { getParams } = Utils;

import Utils from "../utils";
Utils.getParams
*/
